import { Suspense, useLayoutEffect, useState, useEffect } from 'react'
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom'
import { Toaster } from 'sonner'
import { AuthProvider, useAuth } from './contexts/AuthContext'
import LayoutAdmin   from './components/layout/LayoutAdmin'
import LayoutCliente from './components/layout/LayoutCliente'
import ErrorBoundary from './components/ErrorBoundary'
import RouteContentFallback from './components/ui/RouteContentFallback'
import { beginRequestScope } from './utils/api'
import { lazyWithRetry } from './utils/lazyWithRetry'
import Login from './pages/Login'
const DashboardAdmin = lazyWithRetry(() => import('./pages/admin/Dashboard'))
const Clientes = lazyWithRetry(() => import('./pages/admin/Clientes'))
const DetalheCliente = lazyWithRetry(() => import('./pages/admin/DetalheCliente'))
const DetalheContrato = lazyWithRetry(() => import('./pages/admin/DetalheContrato'))
const Configuracoes = lazyWithRetry(() => import('./pages/admin/Configuracoes'))
const Financeiro = lazyWithRetry(() => import('./pages/admin/Financeiro'))
const Usuarios = lazyWithRetry(() => import('./pages/Usuarios'))
const Auditoria = lazyWithRetry(() => import('./pages/admin/Auditoria'))
const SaudeOperacional = lazyWithRetry(() => import('./pages/admin/SaudeOperacional'))

const DashboardTecnico = lazyWithRetry(() => import('./pages/tecnico/Dashboard'))
const Agenda = lazyWithRetry(() => import('./pages/tecnico/Agenda'))
const DashboardCliente = lazyWithRetry(() => import('./pages/cliente/Dashboard'))
const Chamados = lazyWithRetry(() => import('./pages/Chamados'))
const DetalheChamado = lazyWithRetry(() => import('./pages/DetalheChamado'))
const Fechamento = lazyWithRetry(() => import('./pages/Fechamento'))
const FaturaDetalhe = lazyWithRetry(() => import('./pages/FaturaDetalhe'))
const Perfil = lazyWithRetry(() => import('./pages/Perfil'))
const Hoje = lazyWithRetry(() => import('./pages/Hoje'))
const RotinasPreventivas = lazyWithRetry(() => import('./pages/RotinasPreventivas'))
const Ativos = lazyWithRetry(() => import('./pages/Ativos'))
const Conhecimento = lazyWithRetry(() => import('./pages/Conhecimento'))
const CrmBoard = lazyWithRetry(() => import('./pages/CRM/Board'))
const CrmAnalytics = lazyWithRetry(() => import('./pages/CRM/Analytics'))
const CrmProdutividade = lazyWithRetry(() => import('./pages/CRM/Produtividade'))
const CrmConfiguracoes = lazyWithRetry(() => import('./pages/CRM/Configuracoes'))
const CatalogoPrecificacao = lazyWithRetry(() => import('./pages/CRM/CatalogoPrecificacao'))
const CatalogoPlanos = lazyWithRetry(() => import('./pages/CRM/CatalogoPlanos'))
const CatalogoModulos = lazyWithRetry(() => import('./pages/CRM/CatalogoModulos'))
const EstoqueOperacao = lazyWithRetry(() => import('./pages/EstoqueOperacao'))
const EstoqueReservas = lazyWithRetry(() => import('./pages/EstoqueReservas'))
const EstoqueInventarios = lazyWithRetry(() => import('./pages/EstoqueInventarios'))
const CrmDetalhe = lazyWithRetry(() => import('./pages/CRM/OportunidadeDetalhe'))
const OrcamentoPublico = lazyWithRetry(() => import('./pages/CRM/OrcamentoPublico'))
const RouteState = lazyWithRetry(() => import('./pages/RouteState'))
const AceitarConvite = lazyWithRetry(() => import('./pages/AceitarConvite'))
const AlteracaoSenhaObrigatoria = lazyWithRetry(() => import('./pages/AlteracaoSenhaObrigatoria'))
const BancoSenhas = lazyWithRetry(() => import('./pages/BancoSenhas'))
const Pessoas = lazyWithRetry(() => import('./pages/Pessoas'))
const GestaoPonto = lazyWithRetry(() => import('./pages/GestaoPonto'))
const CentralAjuda = lazyWithRetry(() => import('./pages/cliente/CentralAjuda'))
const AtendimentoWhatsApp = lazyWithRetry(() => import('./pages/admin/AtendimentoWhatsApp'))
const ChatwootDashboardApp = lazyWithRetry(() => import('./pages/chatwoot/ChatwootDashboardApp'))
const DiretoriaIA = lazyWithRetry(() => import('./pages/admin/DiretoriaIA'))
const PoliticaPrivacidade = lazyWithRetry(() => import('./pages/public/PoliticaPrivacidade'))
const TermosServico = lazyWithRetry(() => import('./pages/public/TermosServico'))
const knownRestrictedRoutes = ['/hoje','/rotinas','/gestao','/fechamento','/financeiro','/clientes','/senhas','/pessoas','/usuarios','/auditoria','/saude','/configuracoes','/agenda','/crm','/produtos','/atendimento','/diretoria-ia']

function PageFallback() {
  return (
    <div className="app-loading-screen" role="status" aria-live="polite" aria-label="Preparando o sistema">
      <div className="app-loading-brand" aria-hidden="true"><span>A</span><i/><span>T</span></div>
      <div className="app-loading-copy"><strong>Aden Technology</strong><span className="spell-shimmer-text">Preparando seu ambiente</span></div>
      <div className="app-loading-progress" aria-hidden="true"><i/><i/><i/></div>
    </div>
  )
}

function AppRoutes() {
  const { user, features, loading, hasModule } = useAuth()
  const location = useLocation()
  // Alterar paginação ou filtros mantém a mesma tela e não deve cancelar a
  // consulta que acabou de ser iniciada por essa própria atualização da URL.
  useLayoutEffect(()=>beginRequestScope(location.pathname),[location.pathname])
  if (loading) return <PageFallback/>
  if (!user) return (
    <Suspense fallback={<PageFallback/>}>
      <Routes>
        <Route path="/login" element={<Login/>}/>
        <Route path="/redefinir-senha" element={<Login/>}/>
        <Route path="/orcamento/:token" element={<OrcamentoPublico/>}/>
        <Route path="/aceitar-convite/:token" element={<AceitarConvite/>}/>
        <Route path="/privacidade" element={<PoliticaPrivacidade/>}/>
        <Route path="/politica-de-privacidade" element={<PoliticaPrivacidade/>}/>
        <Route path="/privacy" element={<PoliticaPrivacidade/>}/>
        <Route path="/termos" element={<TermosServico/>}/>
        <Route path="/termos-de-servico" element={<TermosServico/>}/>
        <Route path="/terms" element={<TermosServico/>}/>
        <Route path="/chatwoot/dashboard-app" element={<ChatwootDashboardApp/>}/>
        <Route path="/chatwoot-widget/ticket" element={<ChatwootDashboardApp/>}/>
        <Route path="*" element={<Navigate to="/login" replace/>}/>
      </Routes>
    </Suspense>
  )
  const isAdmin=user.role==='admin', isFinanceiro=user.role==='financeiro', isTecnico=user.role==='tecnico', isCliente=user.role==='cliente'
  const isClienteAdmin = isCliente && (user.role_cliente||'comum')==='administrador'
  const restricted = knownRestrictedRoutes.some(route => location.pathname === route || location.pathname.startsWith(`${route}/`))
  const LA=LayoutAdmin, LC=LayoutCliente
  const D=(isAdmin||isFinanceiro) ? DashboardAdmin : isTecnico ? DashboardTecnico : DashboardCliente
  const ML=isCliente ? LC : LA
  if (user.must_change_password) {
    return (
      <Suspense fallback={<PageFallback/>}>
        <Routes>
          <Route path="/alterar-senha-obrigatoria" element={<AlteracaoSenhaObrigatoria/>}/>
          <Route path="*" element={<Navigate to="/alterar-senha-obrigatoria" replace state={{ from: location.pathname }}/>}/>
        </Routes>
      </Suspense>
    )
  }
  return (
    <Suspense fallback={<RouteContentFallback/>}>
    <Routes>
      <Route path="/login" element={<Navigate to="/" replace/>}/>
      <Route path="/alterar-senha-obrigatoria" element={<Navigate to="/" replace/>}/>
      <Route path="/hoje" element={isCliente ? <Navigate to="/" replace/> : hasModule('hoje') ? <ML><Hoje/></ML> : <ML><RouteState forbidden/></ML>}/>
      <Route path="/" element={hasModule('dashboard') ? <ML><D/></ML> : <Navigate to={hasModule('chamados') ? '/chamados' : '/perfil'} replace/>}/>
      <Route path="/chamados" element={hasModule('chamados') ? <ML><Chamados/></ML> : <ML><RouteState forbidden/></ML>}/>
      <Route path="/chamados/:id" element={hasModule('chamados') ? <ML><DetalheChamado/></ML> : <ML><RouteState forbidden/></ML>}/>
      {!isCliente && <>
        <Route path="/atendimento" element={<LA><AtendimentoWhatsApp initialTab="chat"/></LA>}/>
        <Route path="/atendimento/chat" element={<Navigate to="/atendimento" replace/>}/>
        <Route path="/atendimento/conexao" element={<LA><AtendimentoWhatsApp initialTab="conexao"/></LA>}/>
        <Route path="/atendimento/agendamentos" element={<LA><AtendimentoWhatsApp initialTab="agendamentos"/></LA>}/>
        <Route path="/atendimento/campanhas" element={<LA><AtendimentoWhatsApp initialTab="campanhas"/></LA>}/>
        <Route path="/atendimento/auto-replies" element={<LA><AtendimentoWhatsApp initialTab="autoreplies"/></LA>}/>
        <Route path="/atendimento/seguranca" element={<LA><AtendimentoWhatsApp initialTab="accesscontrol"/></LA>}/>
        <Route path="/atendimento/regua" element={<LA><AtendimentoWhatsApp initialTab="regua"/></LA>}/>
        <Route path="/atendimento/configuracoes" element={<LA><AtendimentoWhatsApp initialTab="config"/></LA>}/>
      </>}
      {!isCliente && hasModule('rotinas') && <Route path="/rotinas" element={<ML><RotinasPreventivas/></ML>}/>}
      {!isCliente && hasModule('gestao') && <><Route path="/ativos" element={<ML><Ativos/></ML>}/><Route path="/conhecimento" element={<ML><Conhecimento/></ML>}/></>}
      {!isCliente && <Route path="/diretoria-ia" element={<LA><DiretoriaIA/></LA>}/>}
      {!isCliente && hasModule('senhas') && <Route path="/senhas" element={<ML><BancoSenhas/></ML>}/>}
      <Route path="/perfil" element={<ML><Perfil/></ML>}/>
      {isCliente && <Route path="/ajuda" element={<LC><CentralAjuda/></LC>}/>}
      
      {!isCliente && hasModule('financeiro') && <Route path="/fechamento" element={<Navigate to="/financeiro" replace/>}/>}
      {isClienteAdmin && <Route path="/fechamento" element={<LC><Fechamento/></LC>}/>}
      {((!isCliente && hasModule('financeiro'))||isClienteAdmin) && <>
        <Route path="/fechamento/:id" element={<ML><FaturaDetalhe/></ML>}/>
      </>}
      {!isCliente && <>
        {hasModule('clientes') && <><Route path="/clientes" element={<LA><Clientes/></LA>}/><Route path="/clientes/:id" element={<LA><DetalheCliente/></LA>}/><Route path="/contratos/:id" element={<LA><DetalheContrato/></LA>}/></>}
        {hasModule('usuarios') && <Route path="/usuarios" element={<LA><Usuarios/></LA>}/>}
        {hasModule('auditoria') && <Route path="/auditoria" element={<LA><Auditoria/></LA>}/>}
        {hasModule('saude') && <Route path="/saude" element={<LA><SaudeOperacional/></LA>}/>}
        {hasModule('configuracoes') && <Route path="/configuracoes" element={<LA><Configuracoes/></LA>}/>}
        {hasModule('financeiro') && <>
          <Route path="/financeiro" element={<LA><Financeiro section="resumo"/></LA>}/>
          <Route path="/financeiro/transacoes" element={<LA><Financeiro section="resumo"/></LA>}/>
          <Route path="/financeiro/pagar" element={<LA><Financeiro section="pagar"/></LA>}/>
          <Route path="/financeiro/faturas" element={<LA><Financeiro section="faturas"/></LA>}/>
          <Route path="/financeiro/contas" element={<LA><Financeiro section="contas"/></LA>}/>
          <Route path="/financeiro/tesouraria" element={<LA><Financeiro section="contas"/></LA>}/>
          <Route path="/financeiro/conciliacao" element={<LA><Financeiro section="conciliacao"/></LA>}/>
          <Route path="/financeiro/fluxo" element={<LA><Financeiro section="fluxo"/></LA>}/>
          <Route path="/financeiro/dre" element={<LA><Financeiro section="dre"/></LA>}/>
          <Route path="/financeiro/plano-contas" element={<LA><Financeiro section="plano"/></LA>}/>
          <Route path="/financeiro/plano" element={<LA><Financeiro section="plano"/></LA>}/>
          <Route path="/financeiro/operacao" element={<LA><Financeiro section="operacao"/></LA>}/>
          <Route path="/financeiro/capacidade" element={<LA><Financeiro section="capacidade"/></LA>}/>
          <Route path="/capacidade" element={<LA><Financeiro section="capacidade"/></LA>}/>
          <Route path="/financeiro/precificacao" element={<LA><Financeiro section="precificacao"/></LA>}/>
        </>}
        {hasModule('agenda') && <Route path="/agenda" element={<LA><Agenda/></LA>}/>}
      </>}
      {!isCliente && hasModule('crm') && features.crm?.enabled && (
        <>
          <Route path="/crm" element={<LA><CrmBoard/></LA>}/>
          <Route path="/crm/analytics" element={<LA><CrmAnalytics/></LA>}/>
          <Route path="/crm/atendimento" element={<Navigate to="/atendimento" replace/>}/>
          <Route path="/crm/campanhas" element={<Navigate to="/atendimento/campanhas" replace/>}/>
          <Route path="/crm/agendamentos" element={<Navigate to="/atendimento/agendamentos" replace/>}/>
          <Route path="/crm/auto-replies" element={<Navigate to="/atendimento/auto-replies" replace/>}/>
          <Route path="/crm/seguranca" element={<Navigate to="/atendimento/seguranca" replace/>}/>
          <Route path="/crm/configuracoes-whatsapp" element={<Navigate to="/atendimento/configuracoes" replace/>}/>
          {(isAdmin || isFinanceiro) && <Route path="/crm/produtividade" element={<LA><CrmProdutividade/></LA>}/>}
          {(isAdmin || isFinanceiro) && <Route path="/crm/configuracoes" element={<LA><CrmConfiguracoes/></LA>}/>}
          <Route path="/crm/oportunidade/:id" element={<LA><CrmDetalhe/></LA>}/>
        </>
      )}
      {!isCliente && hasModule('produtos_estoque') && <>
        <Route path="/produtos" element={<LA><CatalogoPrecificacao/></LA>}/>
        <Route path="/produtos/planos" element={<LA><CatalogoPlanos/></LA>}/>
        <Route path="/produtos/modulos" element={<LA><CatalogoModulos/></LA>}/>
        <Route path="/produtos/estoque" element={<LA><EstoqueOperacao section="estoque"/></LA>}/>
        <Route path="/produtos/compras" element={<LA><EstoqueOperacao section="compras"/></LA>}/>
        <Route path="/produtos/reservas" element={<LA><EstoqueReservas/></LA>}/>
        <Route path="/produtos/inventarios" element={<LA><EstoqueInventarios/></LA>}/>
        <Route path="/crm/catalogo" element={<Navigate to="/produtos" replace/>}/>
        <Route path="/crm/catalogo/planos" element={<Navigate to="/produtos/planos" replace/>}/>
        <Route path="/crm/catalogo/modulos" element={<Navigate to="/produtos/modulos" replace/>}/>
      </>}
      {!isCliente && hasModule('pessoas') && (user.role !== 'tecnico' || user.bate_ponto) && <>
        <Route path="/pessoas" element={<Navigate to={user.role === 'tecnico' ? '/pessoas/ponto' : '/pessoas/equipe'} replace/>}/>
        <Route path="/pessoas/ponto" element={<LA><Pessoas/></LA>}/>
        <Route path="/pessoas/folha" element={<LA><Pessoas/></LA>}/>
        <Route path="/pessoas/equipe" element={<LA><Pessoas/></LA>}/>
        <Route path="/pessoas/ferias" element={<LA><Pessoas/></LA>}/>
        <Route path="/pessoas/folha-pagamento" element={<LA><Pessoas/></LA>}/>
        <Route path="/pessoas/rescisoes" element={<LA><Pessoas/></LA>}/>
        {user.role === 'admin' && <Route path="/pessoas/gestao-ponto" element={<LA><GestaoPonto/></LA>}/>}
        <Route path="/pessoas/acertos" element={<LA><Pessoas/></LA>}/>
        <Route path="/pessoas/configuracao" element={<LA><Pessoas/></LA>}/>
      </>}
      <Route path="/orcamento/:token" element={<OrcamentoPublico/>}/>
      <Route path="/aceitar-convite/:token" element={<AceitarConvite/>}/>
      <Route path="/privacidade" element={<PoliticaPrivacidade/>}/>
      <Route path="/politica-de-privacidade" element={<PoliticaPrivacidade/>}/>
      <Route path="/privacy" element={<PoliticaPrivacidade/>}/>
      <Route path="/termos" element={<TermosServico/>}/>
      <Route path="/termos-de-servico" element={<TermosServico/>}/>
      <Route path="/terms" element={<TermosServico/>}/>
      <Route path="/chatwoot/dashboard-app" element={<ChatwootDashboardApp/>}/>
      <Route path="/chatwoot-widget/ticket" element={<ChatwootDashboardApp/>}/>
      <Route path="*" element={<ML><RouteState forbidden={restricted}/></ML>}/>
    </Routes>
    </Suspense>
  )
}

function AppContent() {
  const location = useLocation()
  return (
    <ErrorBoundary resetKey={location.pathname}>
      <AppRoutes/>
    </ErrorBoundary>
  )
}

function useCurrentTheme() {
  const [theme, setTheme] = useState(() => {
    if (typeof document === 'undefined') return 'dark'
    return document.documentElement.dataset.theme || localStorage.getItem('theme') || 'dark'
  })

  useEffect(() => {
    const root = document.documentElement
    const update = () => setTheme(root.dataset.theme || localStorage.getItem('theme') || 'dark')
    const observer = new MutationObserver(update)
    observer.observe(root, { attributes: true, attributeFilter: ['data-theme'] })
    window.addEventListener('storage', update)
    return () => {
      observer.disconnect()
      window.removeEventListener('storage', update)
    }
  }, [])

  return theme
}

export default function App() {
  const currentTheme = useCurrentTheme()

  return (
    <BrowserRouter>
      <AuthProvider>
        <a href="#conteudo-principal" className="skip-link">Ir para o conteúdo principal</a>
        <Toaster
          position="top-center"
          theme={currentTheme}
          closeButton
          duration={3500}
          offset="68px"
        />
        <AppContent/>
      </AuthProvider>
    </BrowserRouter>
  )
}
